// frontend/packages/business-console/src/pages/WebsiteBuild.jsx
//
// Customer-facing "Website Build" hub. Customers start their build on the
// marketing site (the purchase funnel), but before this page there was no
// way back INTO the funnel from the Console: a customer who closed the tab
// mid-build, or who came back to finish going live, had no re-entry point.
// This page is that door.
//
// It reads the lightweight build status (GET /v2/build/status; the same
// endpoint BuildProgressBanner uses on Home) and renders one contextual
// card that deep-links to the right step:
//   interview -> /business/:slug/build
//   review    -> /business/:slug/build/review
//   go_live   -> /business/:slug/build/go-live
//   live      -> a "your site is live" confirmation (no CTA)
//   none      -> an empty state pointing back to the marketing funnel
//
// The three build step pages stay hideFromNav (reached only through this
// hub or a notification); this is the single nav-visible entry. It
// self-gates: the backend degrade-opens to stage 'none' for non-admins and
// tenants with no build, so it is safe to render for everyone (P4).
//
// Advances P1 (customers self-serve re-entry into the build funnel, no
// founder hand-holding to find their half-finished build).

import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import { getBuildStatus } from '../services/buildFunnelService';
import { useTokens, PageHeader, Surface } from '../components/primitives';

const BRAND_GRADIENT =
  'linear-gradient(120deg, #2563eb 0%, #7c3aed 55%, #ab2ddd 100%)';

// One entry per actionable stage. `routeSuffix` comes from the status
// payload, but we key copy off `stage` so the wording is deliberate.
const STAGE_COPY = {
  interview: {
    eyebrow: 'In progress',
    title: 'Finish building your website',
    body: 'Answer a few quick questions and our AI drafts your site for you. It takes about 5 minutes.',
    cta: 'Continue building',
  },
  review: {
    eyebrow: 'Ready to review',
    title: 'Your website draft is ready',
    body: 'Take a look at what we built. Approve it, or tell us what to change.',
    cta: 'Review my site',
  },
  go_live: {
    eyebrow: 'Almost there',
    title: 'You are almost live',
    body: 'One last step: connect your domain to publish your site.',
    cta: 'Finish going live',
  },
};

export default function WebsiteBuild() {
  const t = useTokens();
  const navigate = useNavigate();
  const { slug } = useParams();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getBuildStatus()
      .then((s) => {
        if (alive) setStatus(s);
      })
      .catch(() => {
        // Degrade-open: treat any error as "no active build".
        if (alive) setStatus({ hasActiveBuild: false, stage: 'none' });
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const header = (
    <PageHeader
      title="Website Build"
      meta={
        <Typography sx={{ color: t.muted, fontSize: 13.5 }}>
          Pick up your website build wherever you left off.
        </Typography>
      }
      sx={{ mb: 2 }}
    />
  );

  if (loading) {
    return (
      <Box>
        {header}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={26} />
        </Box>
      </Box>
    );
  }

  const stage = status?.stage || 'none';

  // Live: the site is published. No action, just a confirmation.
  if (stage === 'live') {
    return (
      <Box>
        {header}
        <Surface
          sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}
        >
          <CheckCircleOutlineIcon sx={{ color: t.green, mt: '2px' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
              Your website is live
            </Typography>
            <Typography sx={{ color: t.muted, fontSize: 14, mt: 0.5 }}>
              Your site is published and serving visitors. To make changes, use
              UpRyzze AI or the Content tools in the Website section.
            </Typography>
          </Box>
        </Surface>
      </Box>
    );
  }

  const copy = STAGE_COPY[stage];

  // None (or an unknown stage): nothing in flight. Point back to the funnel.
  if (!copy || !status?.hasActiveBuild || !status?.routeSuffix) {
    return (
      <Box>
        {header}
        <Surface sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 0.5 }}>
            No website build in progress
          </Typography>
          <Typography sx={{ color: t.muted, fontSize: 14, mb: 2 }}>
            Once you start a website build, you can come back here any time to
            pick up where you left off. Start your build on upryzze.com.
          </Typography>
          <Button
            variant="outlined"
            endIcon={<OpenInNewOutlinedIcon />}
            href="https://upryzze.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Start your build
          </Button>
        </Surface>
      </Box>
    );
  }

  // Actionable stage: interview / review / go_live.
  const go = () => navigate(`/business/${slug}/${status.routeSuffix}`);

  return (
    <Box>
      {header}
      <Box
        onClick={go}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') go();
        }}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 3,
          cursor: 'pointer',
          color: '#fff',
          backgroundImage: BRAND_GRADIENT,
          boxShadow: '0 12px 30px rgba(124, 92, 246, 0.30)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <RocketLaunchOutlinedIcon sx={{ fontSize: 34, opacity: 0.9 }} />
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              opacity: 0.85,
            }}
          >
            {copy.eyebrow}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 0.5 }}>
            {copy.title}
          </Typography>
          <Typography
            sx={{ fontSize: 14.5, mt: 0.5, opacity: 0.92, lineHeight: 1.5 }}
          >
            {copy.body}
          </Typography>
        </Box>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            go();
          }}
          variant="contained"
          sx={{
            bgcolor: '#fff',
            color: '#4c1d95',
            fontWeight: 800,
            px: 3,
            py: 1.2,
            '&:hover': { bgcolor: '#f3f0ff' },
          }}
        >
          {copy.cta}
        </Button>
      </Box>
    </Box>
  );
}
